import { createLiveStore, useRpc } from "../lib/live-store";
import type { User } from "../types/api";

interface UserListFilter {
  status?: string;
  role?: string;
  search?: string;
  registration_status?: string;
}

interface UserListResponse {
  data: User[];
  total: number;
  limit: number;
  offset: number;
}

interface UserStats {
  total: number;
  byRole: { Admin: number; Editor: number; Manager: number; Viewer: number; External: number };
  byStatus: { invited: number; pending_approval: number; active: number; disabled: number };
}

interface UserListData {
  users: UserListResponse | null;
}

interface UserListExtra {
  filter: UserListFilter;
  setFilter: (filter: UserListFilter) => void;
}

export const useUserListStore = createLiveStore<UserListData, UserListExtra>({
  topic: "users.list",
  params: (s) => s.filter,
  onSnapshot: (data) => ({ users: data as UserListResponse }),
  onEvent: (event, state) => {
    if (!state.users) return null;
    if (typeof event !== "object" || !event) return null;
    const evt = event as { action?: string; data?: User | User[]; id?: number };
    if (!evt.action) return { users: event as UserListResponse };

    switch (evt.action) {
      case "created":
        if (evt.data && !Array.isArray(evt.data)) {
          return {
            users: {
              ...state.users,
              data: [evt.data, ...state.users.data],
              total: state.users.total + 1,
            },
          };
        }
        return null;
      case "updated":
        if (evt.data && evt.id !== undefined && !Array.isArray(evt.data)) {
          const updated = state.users.data.map((u) =>
            u.user_id === evt.id ? (evt.data as User) : u
          );
          return { users: { ...state.users, data: updated } };
        }
        return null;
      case "deleted":
        if (evt.id !== undefined) {
          const filtered = state.users.data.filter((u) => u.user_id !== evt.id);
          if (filtered.length < state.users.data.length) {
            return {
              users: { ...state.users, data: filtered, total: state.users.total - 1 },
            };
          }
        }
        return {};
    }
    return {};
  },
  state: (set) => ({
    users: null,
    filter: {} as UserListFilter,
    setFilter: (filter: UserListFilter) => set({ filter } as any),
  }),
});

interface UserStatsData {
  stats: UserStats | null;
}

export const useUserStatsStore = createLiveStore<UserStatsData>({
  topic: "users.stats",
  onSnapshot: (data) => ({ stats: data as UserStats }),
  state: () => ({ stats: null }),
});

interface ActiveUsersData {
  activeUsers: User[];
}

export const useActiveUsersStore = createLiveStore<ActiveUsersData>({
  topic: "users.list",
  params: () => ({ registration_status: "active" }),
  onSnapshot: (data) => {
    if (data && typeof data === "object" && "data" in (data as any)) {
      return { activeUsers: (data as any).data as User[] };
    }
    return { activeUsers: data as User[] };
  },
  onEvent: () => null,
  state: () => ({ activeUsers: [] }),
});

export function useApproveUser() { return useRpc("user.approve"); }
export function useRejectUser() { return useRpc("user.reject"); }
export function useDisableUser() { return useRpc("user.disable"); }
export function useEnableUser() { return useRpc("user.enable"); }
export function useDeleteUser() { return useRpc("user.delete"); }
export function useResetPassword() { return useRpc("user.resetPassword"); }
export function useResendInvite() { return useRpc("user.resendInvite"); }
export function useUpdateUser() { return useRpc("user.update"); }
export function useInviteUser() { return useRpc("user.invite"); }
